import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AppIcon,
  BriefcaseBusinessIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CompassIcon,
  LogoutIcon,
  MenuIcon,
  RefreshCWIcon,
  SearchIcon,
  SettingsIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UserIcon,
  XIcon,
} from '../../components/icons';
import { NotificationInbox } from './NotificationInbox';
import { AvatarProfilePhoto } from '../../components/base/avatar/avatar';
import { prefetchDashboardPath } from '../../app/routes/dashboardChunkPrefetch';
import { SidebarNavItems } from './SidebarNavItems';
import type { NavEntry } from './navConfig';

export type DashboardSidebarProps = {
  sidebarCollapsed: boolean;
  mobileSidebarOpen: boolean;
  showReferralSwitcher: boolean;
  dashboardMode: 'jobseeker' | 'employee';
  navEntries: NavEntry[];
  isActive: (path: string) => boolean;
  showSidebarUpgradeCard: boolean;
  upgradeRequestStatus: string | null;
  onToggleCollapse: () => void;
  onCloseMobile: () => void;
  onSwitchMode: (mode: 'jobseeker' | 'employee') => void;
  onLogout: () => void;
  onOpenUpgrade: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
};

export const DashboardSidebar = memo(function DashboardSidebar({
  sidebarCollapsed,
  mobileSidebarOpen,
  showReferralSwitcher,
  dashboardMode,
  navEntries,
  isActive,
  showSidebarUpgradeCard,
  upgradeRequestStatus,
  onToggleCollapse,
  onCloseMobile,
  onSwitchMode,
  onLogout,
  onOpenUpgrade,
  onMouseEnter,
  onMouseLeave,
}: DashboardSidebarProps) {
  return (
    <aside
      className={`dash-sidebar ${mobileSidebarOpen ? 'dash-sidebar--mobile-open' : ''}`}
      aria-label="Dashboard navigation"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="dash-sidebar__brand">
        <div className="dash-sidebar__logo">
          <img src="/images/logo.png" alt="ReffConnect" className="dash-sidebar__logo-img" loading="eager" />
          {!sidebarCollapsed && <span className="dash-sidebar__brand-name">REFFCONNECT</span>}
        </div>
        <button
          type="button"
          className="dash-sidebar__collapse-btn d-none-mobile"
          onClick={onToggleCollapse}
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <AppIcon
            icon={ChevronRightIcon}
            className={`dash-sidebar__collapse-icon ${sidebarCollapsed ? '' : 'rotated'}`}
            size={14}
          />
        </button>
        <button
          type="button"
          className="dash-sidebar__close-btn d-none-desktop"
          onClick={onCloseMobile}
          aria-label="Close sidebar"
        >
          <AppIcon icon={XIcon} size={22} />
        </button>
      </div>

      {showReferralSwitcher && (
        <div className="dash-sidebar__switcher" data-tour="mode-switcher">
          <button
            type="button"
            className={`dash-mode-btn ${dashboardMode === 'jobseeker' ? 'dash-mode-btn--active' : ''}`}
            onClick={() => onSwitchMode('jobseeker')}
            title={sidebarCollapsed ? 'Want a Referral' : undefined}
          >
            {!sidebarCollapsed ? <span className="dash-mode-btn__text">Want a Referral</span> : <AppIcon icon={SearchIcon} size={16} />}
          </button>
          <button
            type="button"
            className={`dash-mode-btn ${dashboardMode === 'employee' ? 'dash-mode-btn--active' : ''}`}
            onClick={() => onSwitchMode('employee')}
            title={sidebarCollapsed ? 'Want to Refer' : undefined}
          >
            {!sidebarCollapsed ? <span className="dash-mode-btn__text">Want to Refer</span> : <AppIcon icon={RefreshCWIcon} size={16} />}
          </button>
        </div>
      )}

      <nav className="dash-sidebar__nav">
        {!sidebarCollapsed && <span className="dash-sidebar__nav-label">Overview</span>}
        <SidebarNavItems entries={navEntries} isActive={isActive} sidebarCollapsed={sidebarCollapsed} />

        <Link
          to="/settings"
          onMouseEnter={() => prefetchDashboardPath('/settings')}
          onFocus={() => prefetchDashboardPath('/settings')}
          className={`dash-nav-item ${isActive('/settings') ? 'dash-nav-item--active' : ''}`}
          title={sidebarCollapsed ? 'Settings' : undefined}
        >
          <AppIcon icon={SettingsIcon} className="dash-nav-item__icon" size={15} />
          {!sidebarCollapsed && <span className="dash-nav-item__label">Settings</span>}
        </Link>
        <button
          type="button"
          className="dash-nav-item dash-nav-item--logout"
          onClick={onLogout}
          title={sidebarCollapsed ? 'Logout' : undefined}
        >
          <AppIcon icon={LogoutIcon} className="dash-nav-item__icon" size={15} />
          {!sidebarCollapsed && <span className="dash-nav-item__label">Logout</span>}
        </button>
      </nav>

      {showSidebarUpgradeCard && (
        <div className="dash-sidebar__footer">
          <div
            className={`dash-sidebar__upgrade ${
              upgradeRequestStatus === 'pending' ? 'dash-sidebar__upgrade--pending' : ''
            }`}
          >
            <span className="dash-sidebar__upgrade-aurora" aria-hidden />
            <span className="dash-sidebar__upgrade-glow" aria-hidden />

            <div className="dash-sidebar__upgrade-head">
              <span className="dash-sidebar__upgrade-icon" aria-hidden>
                <AppIcon icon={BriefcaseBusinessIcon} size={15} />
              </span>
              <span className="dash-sidebar__upgrade-eyebrow">Career milestone</span>
            </div>

            <h3 className="dash-sidebar__upgrade-title">Upgrade to Working Professional</h3>
            <p className="dash-sidebar__upgrade-text">
              Got hired somewhere? Unlock your working professional dashboard on ReffConnect.
            </p>

            <button
              type="button"
              className={`dash-sidebar__upgrade-btn ${
                upgradeRequestStatus === 'pending' ? 'dash-sidebar__upgrade-btn--pending' : ''
              }`}
              onClick={onOpenUpgrade}
              disabled={upgradeRequestStatus === 'pending'}
            >
              {upgradeRequestStatus === 'pending' && (
                <AppIcon icon={RefreshCWIcon} size={14} />
              )}
              <span>
                {upgradeRequestStatus === 'pending' ? 'Request pending' : 'Upgrade Now'}
              </span>
              {upgradeRequestStatus !== 'pending' && (
                <span className="dash-sidebar__upgrade-btn-shine" aria-hidden />
              )}
            </button>
          </div>
        </div>
      )}
    </aside>
  );
});

export type DashboardTopbarProps = {
  pageTitle: string;
  showTopbarSearch: boolean;
  showHiredButton: boolean;
  hiredButtonLabel: string;
  upgradeRequestStatus: string | null;
  searchQuery: string;
  userName: string;
  userEmail: string;
  userAvatar: string;
  userVerified?: boolean;
  onOpenMobileSidebar: () => void;
  onSearchChange: (value: string) => void;
  onOpenHiredModal: () => void;
  onOpenProfile: () => void;
  onOpenSettings: () => void;
  onStartTour: () => void;
  onSearchClick?: () => void;
};

export const DashboardTopbar = memo(function DashboardTopbar({
  pageTitle,
  showTopbarSearch,
  showHiredButton,
  hiredButtonLabel,
  upgradeRequestStatus,
  searchQuery,
  userName,
  userEmail,
  userAvatar,
  userVerified = false,
  onOpenMobileSidebar,
  onSearchChange,
  onOpenHiredModal,
  onOpenProfile,
  onOpenSettings,
  onStartTour,
  onSearchClick,
}: DashboardTopbarProps) {
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  const closeProfileMenu = useCallback(() => setProfileMenuOpen(false), []);

  useEffect(() => {
    if (!profileMenuOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        closeProfileMenu();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeProfileMenu();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [profileMenuOpen, closeProfileMenu]);

  const handleProfileMenuToggle = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setProfileMenuOpen((open) => !open);
  };

  const handleGoToProfile = () => {
    closeProfileMenu();
    onOpenProfile();
  };

  const handleGoToSettings = () => {
    closeProfileMenu();
    onOpenSettings();
  };

  return (
    <header className={`dash-topbar ${showTopbarSearch ? '' : 'dash-topbar--no-search'}`}>
      <div className="dash-topbar__left">
        <button
          type="button"
          className="dash-topbar__hamburger d-none-desktop"
          onClick={onOpenMobileSidebar}
          aria-label="Open menu"
        >
          <AppIcon icon={MenuIcon} size={20} />
        </button>

        {showHiredButton && (
          <button
            type="button"
            className={`dash-topbar__hired-btn ${
              upgradeRequestStatus === 'pending' ? 'dash-topbar__hired-btn--pending' : ''
            }`}
            onClick={onOpenHiredModal}
            disabled={upgradeRequestStatus === 'pending'}
            data-tour="hired-button"
            aria-label={hiredButtonLabel}
            title={
              upgradeRequestStatus === 'pending'
                ? 'Your upgrade request is under review'
                : 'Unlock your working professional dashboard'
            }
          >
            <span className="dash-topbar__hired-icon" aria-hidden="true">
              <AppIcon
                icon={upgradeRequestStatus === 'pending' ? RefreshCWIcon : SparklesIcon}
                size={14}
              />
            </span>
            <span className="dash-topbar__hired-label">{hiredButtonLabel}</span>
            {upgradeRequestStatus !== 'pending' && (
              <span className="dash-topbar__hired-shine" aria-hidden="true" />
            )}
          </button>
        )}
      </div>

      <div className="dash-topbar__center">
        {showTopbarSearch && (
          <div className="dash-topbar__search-row" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div className="dash-topbar__search" data-tour="topbar-search">
              <AppIcon icon={SearchIcon} className="dash-topbar__search-icon" size={15} />
              <input
                type="text"
                className="dash-topbar__search-input"
                placeholder="Search mentors..."
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                onClick={onSearchClick}
                onFocus={onSearchClick}
              />
            </div>
            <button
              type="button"
              className="rd-tour-btn rd-tour-btn--sm"
              onClick={() => {
                window.dispatchEvent(new CustomEvent('user-open-setup-guide'));
              }}
              style={{
                background: 'rgba(196, 165, 116, 0.15)',
                border: '1px solid rgba(196, 165, 116, 0.35)',
                color: '#fbbf24',
                fontSize: '0.78rem',
                fontWeight: 600,
                padding: '6px 14px',
                borderRadius: '20px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}
            >
              Setup Guide
            </button>
          </div>
        )}
      </div>

      <div className="dash-topbar__right">
        <button
          type="button"
          className="dash-topbar__notif-btn"
          onClick={onStartTour}
          aria-label="Take a product tour"
          title="Take a product tour"
          data-tour="tour-replay"
        >
          <AppIcon icon={CompassIcon} size={17} />
        </button>

        <span className="dash-topbar__inbox" data-tour="notifications">
          <NotificationInbox />
        </span>

        <div className="dash-topbar__profile-group d-none-mobile" ref={profileMenuRef}>
          <button
            type="button"
            className="dash-topbar__profile"
            onClick={onOpenProfile}
            aria-label="Open profile"
          >
            <AvatarProfilePhoto
              verified={userVerified}
              size="xs"
              alt={userName}
              src={userAvatar}
            />
            <span className="dash-topbar__profile-text">
              {userName ? (
                <span className="dash-topbar__user-name-row">
                  <span className="dash-topbar__user-name" title={userName}>
                    {userName}
                  </span>
                  {userVerified && (
                    <span className="dash-topbar__verified" title="Verified account">
                      <AppIcon icon={ShieldCheckIcon} size={11} aria-hidden />
                      Verified
                    </span>
                  )}
                </span>
              ) : null}
              {userEmail ? <span className="dash-topbar__user-email">{userEmail}</span> : null}
            </span>
          </button>
          <button
            type="button"
            className={`dash-topbar__profile-menu-btn ${profileMenuOpen ? 'is-open' : ''}`}
            onClick={handleProfileMenuToggle}
            aria-label="Account menu"
            aria-expanded={profileMenuOpen}
            aria-haspopup="menu"
          >
            <AppIcon icon={ChevronDownIcon} className="dash-topbar__profile-chevron" size={12} />
          </button>

          {profileMenuOpen && (
            <div className="dash-topbar__profile-dropdown" role="menu">
              <button
                type="button"
                className="dash-topbar__profile-dropdown-item"
                role="menuitem"
                onClick={handleGoToProfile}
              >
                <AppIcon icon={UserIcon} size={16} aria-hidden />
                <span>Profile</span>
              </button>
              <button
                type="button"
                className="dash-topbar__profile-dropdown-item"
                role="menuitem"
                onClick={handleGoToSettings}
              >
                <AppIcon icon={SettingsIcon} size={16} aria-hidden />
                <span>Settings</span>
              </button>
            </div>
          )}
        </div>

        <button
          type="button"
          className="dash-topbar__profile dash-topbar__profile--mobile-only"
          onClick={onOpenProfile}
          aria-label="Open profile"
        >
          <AvatarProfilePhoto
            verified={userVerified}
            size="xs"
            alt={userName}
            src={userAvatar}
          />
        </button>
      </div>
    </header>
  );
});
